export type PhoneMask = string | string[];

export const DEFAULT_PHONE_MASK = 'XXXXXXXXXX';

/**
 * Countries whose national numbers come in more than one length, so a single
 * mask from `GET /location/country` cannot hold them. `vue-the-mask` picks the
 * shortest mask that fits the digits typed.
 *
 * Brazil has no `phoneMask` row at all, and the 10-character default cut the
 * last digit off every mobile number (TWISM-21037): landlines are DDD + 8
 * digits, mobiles DDD + 9.
 */
const PHONE_MASKS_BY_COUNTRY_ISO2: Readonly<Record<string, string[]>> = {
  BR: ['(XX) XXXX-XXXX', '(XX) XXXXX-XXXX']
};

export function getPhoneMask(country?: {
  countryIso2?: string;
  phoneMask?: string | null;
}): PhoneMask {
  const iso2 = ((country && country.countryIso2) || '').toUpperCase();

  return (
    PHONE_MASKS_BY_COUNTRY_ISO2[iso2] ||
    (country && country.phoneMask) ||
    DEFAULT_PHONE_MASK
  );
}

/**
 * A single mask for displaying a stored number, for the `VMask` filter, which
 * takes one string: the shortest variant with a slot for every digit.
 */
export function getPhoneDisplayMask(
  country: { countryIso2?: string; phoneMask?: string | null } | undefined,
  digits: string
): string {
  const mask = getPhoneMask(country);
  if (!Array.isArray(mask)) return mask;

  const slots = (variant: string) => variant.replace(/[^X]/g, '').length;
  const bySlots = [...mask].sort((a, b) => slots(a) - slots(b));

  return (
    bySlots.find(variant => slots(variant) >= digits.length) ||
    bySlots[bySlots.length - 1]
  );
}

/** The longest variant, so the placeholder shows every digit the field takes. */
export function getPhoneMaskPlaceholder(mask: PhoneMask): string {
  if (!Array.isArray(mask)) return mask;

  return mask.reduce((longest, current) =>
    current.length > longest.length ? current : longest
  );
}
